import { useState } from "react";
import { Link } from "wouter";
import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import StreakPill from "@/components/mnf/StreakPill";
import StrengthCard from "@/components/mnf/StrengthCard";
import SabbathHeroCard from "@/components/mnf/SabbathHeroCard";
import VerseOfWeekCard from "@/components/mnf/VerseOfWeekCard";
import PartnerCard from "@/components/mnf/PartnerCard";
import MacrosCard from "@/components/mnf/MacrosCard";
import DisciplineBadgesCard from "@/components/mnf/DisciplineBadgesCard";
import ChallengeCard from "@/components/mnf/ChallengeCard";
import AutoflowCard from "@/components/mnf/AutoflowCard";
import SeasonChip from "@/components/mnf/SeasonChip";
import SeasonSetup from "@/components/mnf/SeasonSetup";
import { useStoreQuery, useStoreMutation, queries, mutations, type Tier } from "@/lib/store";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

/* ----------------------- Time helpers (Tonight card) ---------------------- */
function formatClassTime(ts: number) {
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
}
function relativeUntil(ts: number) {
  const diff = ts - Date.now();
  if (diff < 0) return "now";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.round(hrs / 24);
  return `in ${days}d`;
}

/* --------------------------- Bloom headline ------------------------------- */
function bloomHeadline(bloom: { nourish: number; move: number; grace: number } | null | undefined): string {
  const overall = Math.round(((bloom?.nourish ?? 0) + (bloom?.move ?? 0) + (bloom?.grace ?? 0)) / 3);
  if (overall >= 80) return "You're flourishing today";
  if (overall >= 50) return "Good rhythm, sister";
  if (overall >= 25) return "Keep going, gently";
  return "A new day to begin";
}

/* ------------------------------- Bloom Ring ------------------------------- */
function BloomRing({ userId }: { userId: string }) {
  const bloom = useStoreQuery(() => queries.bloomToday(userId));
  // 3 concentric rings: nourish (forest), move (coral), grace (purple)
  const size = 168;
  const stroke = 11;
  const r1 = (size - stroke) / 2;
  const r2 = r1 - stroke - 4;
  const r3 = r2 - stroke - 4;
  const c1 = 2 * Math.PI * r1;
  const c2 = 2 * Math.PI * r2;
  const c3 = 2 * Math.PI * r3;
  const p = (v: number) => v / 100;
  const overall = Math.round(((bloom?.nourish ?? 0) + (bloom?.move ?? 0) + (bloom?.grace ?? 0)) / 3);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r1} fill="none" stroke="rgba(30,74,53,0.10)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r2} fill="none" stroke="rgba(192,120,74,0.14)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r3} fill="none" stroke="rgba(127,119,221,0.16)" strokeWidth={stroke} />
        {/* progress */}
        <circle cx={size / 2} cy={size / 2} r={r1} fill="none" stroke="var(--mnf-f)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${c1 * p(bloom?.nourish ?? 0)} ${c1}`} />
        <circle cx={size / 2} cy={size / 2} r={r2} fill="none" stroke="var(--mnf-t)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${c2 * p(bloom?.move ?? 0)} ${c2}`} />
        <circle cx={size / 2} cy={size / 2} r={r3} fill="none" stroke="var(--mnf-purple)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${c3 * p(bloom?.grace ?? 0)} ${c3}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>Bloom</span>
        <span className="mnf-serif" style={{ fontSize: 34, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}>{overall}%</span>
        <span className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)", marginTop: 2 }}>of today</span>
      </div>
    </div>
  );
}

/* ------------------------ Tonight's class card ---------------------------- */
function TonightClassCard({ cls, userId }: { cls: any; userId: string }) {
  const isLive = cls.status === "live";
  const reminded = useStoreQuery(() => queries.hasReminder(userId, cls.id));
  const toggleReminder = useStoreMutation(mutations.toggleClassReminder);
  const cardContent = (
    <GlassCard
      organic
      padding={0}
      style={{ overflow: "hidden" }}
    >
      <div
        className="relative p-5"
        style={{
          background: isLive
            ? "linear-gradient(135deg, rgba(192,120,74,0.22) 0%, rgba(30,74,53,0.94) 100%)"
            : "linear-gradient(135deg, rgba(127,119,221,0.20) 0%, rgba(30,74,53,0.94) 100%)",
          color: "#F5F0E8",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 mnf-body" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" }}>
            {isLive ? (
              <>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: "#FF6E6E", boxShadow: "0 0 0 4px rgba(255,110,110,0.25)" }} />
                Live now with Coach
              </>
            ) : (
              <>
                <Icon name="videocam" size={12} />
                Tonight's class
              </>
            )}
          </span>
          <span className="mnf-body" style={{ fontSize: 10, fontWeight: 700, color: "rgba(245,240,232,0.85)", padding: "3px 9px", borderRadius: 999, background: "rgba(245,240,232,0.14)", border: "1px solid rgba(245,240,232,0.20)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {isLive ? `${cls.watching} watching` : relativeUntil(cls.scheduledFor)}
          </span>
        </div>
        <h3 className="mnf-serif mt-3" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2 }}>
          {cls.title}
        </h3>
        <p className="mnf-body mt-1" style={{ fontSize: 12, opacity: 0.82 }}>
          {isLive ? "Started" : formatClassTime(cls.scheduledFor)} · {cls.durationMin} min · {cls.tierMin}+
          {cls.verseRef ? ` · ${cls.verseRef}` : ""}
        </p>
        {cls.description && (
          <p className="mnf-body italic mt-2" style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.45 }}>
            {cls.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-4">
          <Link href={`/live/${cls.id}`} className="no-underline" style={{ flex: 1 }} data-testid={`link-tonight-${cls.id}`}>
            <Btn
              variant="primary"
              size="md"
              block
              style={isLive
                ? { background: "#F5F0E8", borderColor: "#F5F0E8", color: "var(--mnf-fd)" }
                : { background: "rgba(245,240,232,0.95)", borderColor: "rgba(245,240,232,0.95)", color: "var(--mnf-fd)" }}
            >
              <Icon name={isLive ? "play_arrow" : "info"} size={16} weight={600} />
              {isLive ? "Join now" : "Details"}
            </Btn>
          </Link>
          {!isLive && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleReminder(userId, cls.id);
              }}
              data-testid={`button-remind-tonight-${cls.id}`}
              className="flex items-center justify-center transition-all hover-elevate"
              style={{
                width: 44, height: 44, borderRadius: 999,
                background: reminded ? "rgba(245,240,232,0.95)" : "rgba(245,240,232,0.14)",
                color: reminded ? "var(--mnf-fd)" : "#F5F0E8",
                border: `1px solid ${reminded ? "rgba(245,240,232,0.95)" : "rgba(245,240,232,0.22)"}`,
                cursor: "pointer",
              }}
              aria-label={reminded ? "Reminder set" : "Remind me"}
              title={reminded ? "Reminder set" : "Remind me"}
            >
              <Icon name={reminded ? "notifications_active" : "notifications"} size={18} weight={600} />
            </button>
          )}
        </div>
      </div>
    </GlassCard>
  );
  return cardContent;
}

/* --------------------------- Mini stat ------------------------------------ */
function MiniStat({ label, value, unit, tone }: { label: string; value: string; unit: string; tone: "forest" | "coral" | "purple" | "fiber" }) {
  const dot: Record<string, string> = {
    forest: "var(--mnf-f)",
    coral: "var(--mnf-t)",
    purple: "var(--mnf-purple)",
    fiber: "#2C6248",
  };
  return (
    <div className="flex items-center gap-2.5">
      <span style={{ width: 8, height: 8, borderRadius: 999, background: dot[tone], flexShrink: 0 }} />
      <div className="flex flex-col leading-tight">
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9.5 }}>{label}</span>
        <span className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)" }}>
          {value}
          <span className="mnf-body" style={{ fontSize: 10, fontWeight: 500, marginLeft: 3, color: "var(--mnf-tg)" }}>{unit}</span>
        </span>
      </div>
    </div>
  );
}

/* --------------------------------- Page ----------------------------------- */
export default function Today() {
  const [showUpgrade, setShowUpgrade] = useState(true);
  const [seasonOpen, setSeasonOpen] = useState(false);
  const liveNow = useStoreQuery(queries.liveNow);
  const upcoming = useStoreQuery(queries.liveUpcoming);
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: an unresolved identity must never read (or,
  // via the cards below, write) Hannah's seeded data. See the early return
  // right before the main render, once every hook above has run.
  const userId = me?.id ?? null;
  const season = useStoreQuery(() => (userId ? queries.seasonState(userId) : null));
  const prayerPulse = useStoreQuery(() => (userId ? queries.prayerWallPulse(userId, 2) : []));
  const sabbathToday = useStoreQuery(() => (userId ? queries.isSabbathToday(userId) : false));
  const notifPrefs = useStoreQuery(() => (userId ? queries.notificationPrefsForUser(userId) : null));
  const sabbathSettings = useStoreQuery(() => (userId ? queries.sabbathFor(userId) : null));
  const todaysSession = useStoreQuery(() =>
    userId ? queries.todaysSession(userId, new Date().toISOString()) : null,
  );
  const movementProfile = useStoreQuery(() => (userId ? queries.movementProfile(userId) : null));
  const bloom = useStoreQuery(() => (userId ? queries.bloomToday(userId) : null));
  // Real food logs (Convex) — same source Garden reads/writes
  const harvestTodayKey = new Date().toLocaleDateString("en-CA");
  const harvestLogs = useConvexQuery(api.nutrition.todayLogs, { dateKey: harvestTodayKey });
  // Hydration + steps (Convex) — same dateKey as harvest
  const hydration = useConvexQuery(api.hydration.todayTotal, { dateKey: harvestTodayKey });
  const stepsRow = useConvexQuery(api.steps.todayTotal, { dateKey: harvestTodayKey });
  const logWaterMutation = useConvexMutation(api.hydration.logWater);
  const logStepsMutation = useConvexMutation(api.steps.logSteps);
  const [waterOpen, setWaterOpen] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [shortcutOpen, setShortcutOpen] = useState(false);
  const stepToken = useConvexQuery(api.steps.getMyToken, {});
  const mintTokenMutation = useConvexMutation(api.steps.mintToken);
  const convexBase = (import.meta.env.VITE_CONVEX_URL as string | undefined) ?? "";
  const httpBase = convexBase.replace(".convex.cloud", ".convex.site");
  const stepsPostUrl = httpBase ? `${httpBase}/steps/log` : "";
  const [waterInput, setWaterInput] = useState("");
  const [stepsInput, setStepsInput] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const totalOz = hydration?.totalOz ?? 0;
  const totalSteps = stepsRow?.steps ?? 0;

  // Returns true only when the write actually landed. Never rejects.
  const addWater = async (oz: number): Promise<boolean> => {
    if (oz <= 0) return false;
    try {
      await logWaterMutation({ dateKey: harvestTodayKey, oz });
      return true;
    } catch (err) {
      console.error("[Today] logging water failed", err);
      Sentry.captureException(err, { tags: { subsystem: "today" } });
      toast({
        variant: "destructive",
        title: "That water didn't save",
        description: `We couldn't add ${oz}oz to today, so your count hasn't moved. Check your connection and tap it again.`,
      });
      return false;
    }
  };
  const saveStepsInput = async (): Promise<void> => {
    const n = parseInt(stepsInput, 10);
    if (!Number.isFinite(n) || n < 0) return;
    try {
      await logStepsMutation({ dateKey: harvestTodayKey, steps: n, source: "manual" });
    } catch (err) {
      console.error("[Today] logging steps failed", err);
      Sentry.captureException(err, { tags: { subsystem: "today" } });
      // Keep her number in the box and the sheet open — nothing was saved.
      toast({
        variant: "destructive",
        title: "Your steps didn't save",
        description: "Today's step count is unchanged. Your number is still here — check your connection and save again.",
      });
      return;
    }
    setStepsInput("");
    setStepsOpen(false);
  };
  const saveCustomWater = async (): Promise<void> => {
    const n = parseFloat(waterInput);
    if (!Number.isFinite(n) || n <= 0) return;
    const saved = await addWater(n);
    if (!saved) return; // leave her amount in the box; addWater already told her
    setWaterInput("");
  };
  const handleStepToken = async (mode: "create" | "rotate"): Promise<void> => {
    if (tokenBusy) return;
    setTokenBusy(true);
    try {
      await mintTokenMutation({});
      toast({
        title: mode === "rotate" ? "Your token was rotated" : "Your token is ready",
        description: mode === "rotate"
          ? "Put the new token into your Shortcut — the old one has stopped working."
          : "Copy it into your iPhone Shortcut below.",
      });
    } catch (err) {
      console.error(`[Today] ${mode === "rotate" ? "rotating" : "creating"} step token failed`, err);
      Sentry.captureException(err, { tags: { subsystem: "today" } });
      toast({
        variant: "destructive",
        title: mode === "rotate" ? "Your token was not rotated" : "We couldn't create your token",
        description: mode === "rotate"
          ? "Nothing changed — your old token is still live and still works. Check your connection and rotate it again."
          : "No token was created yet. Check your connection and try again.",
      });
    } finally {
      setTokenBusy(false);
    }
  };

  const harvestEntries = (harvestLogs ?? []).map((l) => {
    const d = new Date(l.loggedAt);
    let h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const meal = (l.meal ?? "snack") as "morning" | "midday" | "snack" | "evening";
    const emoji = meal === "morning" ? "🌅" : meal === "midday" ? "🥗" : meal === "evening" ? "🍽️" : "🌿";
    return { meal, emoji, time: `${h}:${m} ${ampm}`, name: l.name, kcal: Math.round(l.kcal ?? 0), p: Math.round(l.protein ?? 0) };
  });
  const restingPill = sabbathToday && (sabbathSettings?.pauseStreaks ?? false);
  const tonight = liveNow[0] ?? upcoming[0];
  const launchChallenge = useStoreQuery(queries.launchChallenge);
  const featuredChallenges = useStoreQuery(queries.featuredChallenges) ?? [];
  const todayChallenge = launchChallenge ?? featuredChallenges[0] ?? null;
  const tierLabel = me?.tier ?? "Seedling";
  const tierColor =
    tierLabel === "Flourishing" ? "var(--mnf-gold)" :
    tierLabel === "Rooted"      ? "#A8D4B8" :
                                  "rgba(245,240,232,0.65)";
  const now = new Date();
  const todayEyebrow = `${now.toLocaleDateString("en-US", { weekday: "long" })} \u00b7 ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const sisterName = (me?.name?.split(" ")[0]) ?? "sister";

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Loading your garden...
        </p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <ScreenHeader
        variant="forest"
        eyebrow={todayEyebrow}
        title={<>{greeting},<br /><span className="italic">{sisterName}</span></>}
        subtitle={<>"She is clothed with strength and dignity, and she laughs without fear of the future." <span style={{ opacity: 0.75 }}>Proverbs 31:25</span></>}
        trailing={
          <>
            {/*
              Was a "cycle tier (demo)" BUTTON shipping in production. Two ways
              it hurt: for a signed-in sister it wrote to the seed row and
              visibly did nothing (a control reporting success for work not
              performed), and before auth resolved, currentUser() falls back to
              the demo account — which sits on a PAID tier — so cycling it
              unlocked every gated feature. Tier is display-only now; it changes
              when Stripe says it changes.
            */}
            <div
              className="rounded-full flex items-center justify-center mnf-body"
              style={{
                height: 36, padding: "0 12px",
                background: "rgba(245,240,232,0.14)",
                border: "1px solid rgba(245,240,232,0.22)",
                color: "#F5F0E8",
                fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                cursor: "default",
                gap: 6, display: "flex", alignItems: "center",
              }}
              data-testid="tier-badge"
              aria-label={`Your plan: ${tierLabel}`}
            >
              <span style={{ width: 7, height: 7, borderRadius: 999, background: tierColor }} />
              {tierLabel}
            </div>
            <Link href="/profile" className="no-underline" data-testid="link-profile" aria-label="Open my profile">
              <Avatar
                initials={(me?.name ?? "H").split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                src={me?.avatarDataUrl ?? undefined}
                size={36}
                ringColor="rgba(245,240,232,0.45)"
              />
            </Link>
          </>
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Link href="/cycle" className="no-underline" data-testid="link-cycle">
            <span style={{ cursor: "pointer" }}>
              <PillTag tone="purple">Cycle · Day 14</PillTag>
            </span>
          </Link>
          <StreakPill streak={12} resting={restingPill} restingDays={1} size="sm" />
          <PillTag tone={tierLabel === "Flourishing" ? "gold" : "fiber"}>Tier · {tierLabel}</PillTag>
          {season && season.kind !== "none" ? (
            <SeasonChip
              season={season}
              size="sm"
              variant="onForest"
              onClick={() => setSeasonOpen(true)}
              testId="chip-today-season"
            />
          ) : (
            <button
              onClick={() => setSeasonOpen(true)}
              data-testid="button-today-set-season"
              className="hover-elevate"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "3px 9px", borderRadius: 999,
                background: "rgba(245,240,232,0.12)",
                border: "1px dashed rgba(245,240,232,0.28)",
                color: "#F5F0E8", fontSize: 10.5, fontWeight: 700,
                letterSpacing: "0.04em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <Icon name="spa" size={11} weight={500} />
              Set my season
            </button>
          )}
        </div>
      </ScreenHeader>
      {seasonOpen && (
        <SeasonSetup
          userId={userId}
          current={season}
          onClose={() => setSeasonOpen(false)}
        />
      )}

      <div className="px-5 -mt-3 space-y-4">
        {sabbathToday && (
          <SabbathHeroCard userId={userId} variant="today" />
        )}

        {/* Verse of the week */}
        <VerseOfWeekCard userId={userId} variant="today" />

        {/* 7-day welcome autoflow (only visible to sisters mid-journey) */}
        <AutoflowCard userId={userId} />

        {/* Today's Bloom */}
        <GlassCard padding={20}>
          <div className="flex items-center gap-4">
            <BloomRing userId={userId} />
            <div className="flex-1 space-y-3">
              <div>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Today's Bloom</p>
                <h3 className="mnf-serif" style={{ fontSize: 19, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
                  {bloomHeadline(bloom)}
                </h3>
              </div>
              <div className="space-y-2.5">
                <MiniStat label="Nourish" value={Math.round(harvestEntries.reduce((s, e) => s + e.kcal, 0)).toLocaleString()} unit="kcal" tone="forest" />
                <MiniStat label="Move" value={`${todaysSession?.durationMin ? Math.round((todaysSession.durationMin * (bloom?.move ?? 0)) / 100) : 0}`} unit="min" tone="coral" />
                <MiniStat label="Grace" value={`${Math.round(((bloom?.grace ?? 0) / 100) * 30)}`} unit="min" tone="purple" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4" style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
            <div className="text-center">
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>{Math.round(harvestEntries.reduce((s, e) => s + e.p, 0))}<span className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)", marginLeft: 2 }}>g</span></p>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9 }}>Protein</p>
            </div>
            <button
              type="button"
              onClick={() => setWaterOpen(true)}
              className="text-center"
              style={{ borderLeft: "1px solid rgba(30,74,53,0.08)", borderRight: "1px solid rgba(30,74,53,0.08)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              aria-label="Log water"
            >
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>{totalOz}<span className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)", marginLeft: 2 }}>oz</span></p>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9 }}>Water</p>
            </button>
            <button
              type="button"
              onClick={() => { setStepsInput(String(totalSteps || "")); setStepsOpen(true); }}
              className="text-center"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              aria-label="Log steps"
            >
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>{totalSteps.toLocaleString()}</p>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9 }}>Steps</p>
            </button>
          </div>
        </GlassCard>

        {/* Tonight's class with Coach */}
        {tonight && <TonightClassCard cls={tonight} userId={userId} />}

        {/* Macros for today */}
        <MacrosCard />

        <DisciplineBadgesCard userId={userId} variant="today" />

        {/* Nourish Together challenge */}
        {todayChallenge && (
          <ChallengeCard
            challenge={todayChallenge}
            userId={userId}
            variant="today"
          />
        )}

        {/* Monthly Garden Walk */}
        <GardenWalkTodayCard userId={userId} />

        {/* Kiley's Pick for Your Season */}
        <KileysPickTodayCard userId={userId} />

        {/* Accountability Sister */}
        <PartnerCard />

        {/* Monthly Strength Card */}
        <StrengthCard />

        {/* Up Next */}
        <SectionTitle eyebrow="Coming up" title="Up next" trailing={<Link href="/move" className="no-underline" style={{ color: "var(--mnf-t)" }}>See all</Link>} />
        <GlassCard organic padding={0} style={{ overflow: "hidden" }}>
          <div
            className="relative p-5"
            style={{ background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(168,212,184,0.18) 100%)" }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 52, height: 52,
                  borderRadius: "20px 8px 20px 8px",
                  background: "var(--mnf-f)",
                  color: "#F5F0E8",
                }}
              >
                <Icon name="fitness_center" size={24} weight={500} />
              </div>
              <div className="flex-1">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                  {todaysSession ? `Week ${todaysSession.mesocycleWeek} · Block ${todaysSession.accessoryBlock}` : "Today"}
                </p>
                <h3 className="mnf-serif" style={{ fontSize: 21, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2, marginTop: 2 }}>
                  {todaysSession ? `Strength & Grace, ${todaysSession.focus}` : (movementProfile ? "Rest day" : "Strength & Grace")}
                </h3>
                <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", marginTop: 4 }}>
                  {todaysSession
                    ? `${todaysSession.focus} · ${todaysSession.durationMin} min · ${todaysSession.exercises.length} movements`
                    : (movementProfile ? "Recover and breathe. Back tomorrow." : "Set up your plan to personalize today")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Link href={movementProfile ? "/move" : "/onboarding"} className="no-underline flex-1">
                <Btn variant="primary" block size="md">
                  {todaysSession ? "Begin" : (movementProfile ? "Open Move" : "Set up plan")}
                </Btn>
              </Link>
              <Btn variant="ghost" size="md" data-testid="button-snooze">
                <Icon name="snooze" size={16} />
              </Btn>
            </div>
          </div>
        </GlassCard>

        {/* Daily Word */}
        <SectionTitle eyebrow="Today's word" title="A verse for now" trailing={<Link href="/word" className="no-underline" style={{ color: "var(--mnf-t)" }}>Open</Link>} />
        <Link href="/word" className="no-underline block" data-testid="link-word">
          <GlassCard
            organic
            padding={20}
            style={{
              background: "linear-gradient(135deg, rgba(127,119,221,0.08) 0%, rgba(245,240,232,0.65) 100%)",
              border: "1px solid rgba(127,119,221,0.18)",
            }}
          >
            <div className="flex items-start gap-3">
              <span className="mnf-serif italic shrink-0" style={{ fontSize: 44, color: "var(--mnf-purple)", lineHeight: 0.8 }}>“</span>
              <div className="flex-1">
                <p
                  className="mnf-serif italic"
                  style={{ fontSize: 16, color: "var(--mnf-fd)", lineHeight: 1.45 }}
                >
                  Be still, and know that I am God.
                </p>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)", marginTop: 8 }}>
                  Psalm 46:10 · ESV
                </p>
              </div>
              <Icon name="arrow_forward" size={18} />
            </div>
          </GlassCard>
        </Link>

        {/* Garden snapshot */}
        <SectionTitle eyebrow="The garden" title="Today's harvest" trailing={<Link href="/garden" className="no-underline" style={{ color: "var(--mnf-t)" }}>Garden</Link>} />
        <GlassCard padding={16}>
          <div className="space-y-3">
            {harvestEntries.length === 0 ? (
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", fontStyle: "italic", textAlign: "center", padding: "12px 0" }}>
                Nothing logged yet today. Tap below to add your first bite.
              </p>
            ) : harvestEntries.map((it, i) => (
              <div key={i} className="flex items-center gap-3" style={{ paddingTop: i ? 12 : 0, borderTop: i ? "1px solid rgba(30,74,53,0.06)" : undefined }}>
                <span style={{ fontSize: 22 }} aria-hidden>{it.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="mnf-body truncate" style={{ fontSize: 14, fontWeight: 600, color: "var(--mnf-fd)" }}>{it.name}</p>
                  <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", textTransform: "capitalize" }}>{it.meal} · {it.time}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>{it.kcal}<span className="mnf-body" style={{ fontSize: 9.5, color: "var(--mnf-tg)", marginLeft: 2 }}>kcal</span></p>
                  <p className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)" }}>{it.p}g protein</p>
                </div>
              </div>
            ))}
          </div>
          <Link href="/garden" className="no-underline">
            <Btn variant="ghost" block size="md" className="mt-4">
              <Icon name="add" size={16} /> Add to Garden 🌱
            </Btn>
          </Link>
        </GlassCard>

        {/* Daily Grace check-in */}
        <SectionTitle eyebrow="Grace moment" title="How is your heart?" />
        <GlassCard padding={18}>
          <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            A daily check-in tended by Coach Kiley. Choose what fits today.
          </p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {[
              { key: "radiant",   label: "Radiant",   emoji: "🌻", tone: "var(--mnf-gold)",   bg: "var(--mnf-gold2)" },
              { key: "resting",   label: "Resting",   emoji: "🌿", tone: "#2C6248",            bg: "var(--mnf-fiberBg)" },
              { key: "restoring", label: "Restoring", emoji: "🌧️", tone: "var(--mnf-purple)", bg: "var(--mnf-purpleL)" },
            ].map((m) => (
              <button
                key={m.key}
                data-testid={`button-mood-${m.key}`}
                className="flex flex-col items-center gap-1 py-3 transition-all hover-elevate active-elevate-2"
                style={{
                  borderRadius: 18,
                  background: m.bg,
                  border: "1px solid rgba(30,74,53,0.06)",
                }}
              >
                <span style={{ fontSize: 22 }}>{m.emoji}</span>
                <span className="mnf-body" style={{ fontSize: 11, fontWeight: 700, color: m.tone, letterSpacing: "0.04em", textTransform: "uppercase" }}>{m.label}</span>
              </button>
            ))}
          </div>
          <div
            className="flex items-center gap-3 mt-4 p-3"
            style={{ background: "rgba(127,119,221,0.08)", borderRadius: 16, border: "1px solid rgba(127,119,221,0.14)" }}
          >
            <Avatar initials="K" size={36} ringColor="rgba(127,119,221,0.45)" />
            <div className="flex-1 min-w-0">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>Voice note · Coach Kiley</p>
              <p className="mnf-body italic" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.4, marginTop: 2 }}>
                "Sister, your body is a garden. Tend it with grace today."
              </p>
            </div>
            <button
              data-testid="button-play-voice-note"
              className="flex items-center justify-center shrink-0"
              style={{ width: 36, height: 36, borderRadius: 999, background: "var(--mnf-purple)", color: "#fff" }}
            >
              <Icon name="play_arrow" size={18} weight={600} />
            </button>
          </div>
        </GlassCard>

        {/* Tier upgrade prompt */}
        {showUpgrade && (
          <GlassCard
            padding={16}
            style={{
              background: "linear-gradient(135deg, rgba(160,120,32,0.10) 0%, rgba(245,240,232,0.65) 100%)",
              border: "1px solid rgba(160,120,32,0.20)",
              position: "relative",
            }}
            data-testid="card-upgrade-prompt"
          >
            <button
              data-testid="button-dismiss-upgrade"
              onClick={() => setShowUpgrade(false)}
              className="absolute flex items-center justify-center transition-all hover-elevate"
              style={{
                top: 10, right: 10,
                width: 26, height: 26, borderRadius: 999,
                background: "rgba(160,120,32,0.10)", color: "var(--mnf-gold)",
                cursor: "pointer", border: "none",
              }}
              aria-label="Dismiss"
            >
              <Icon name="close" size={14} />
            </button>
            <div className="flex items-start gap-3" style={{ paddingRight: 24 }}>
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 40, height: 40,
                  borderRadius: "14px 6px 14px 6px",
                  background: "var(--mnf-gold)", color: "#fff",
                }}
              >
                <Icon name="auto_awesome" size={18} weight={500} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>A gentle invitation</p>
                <p
                  className="mnf-serif"
                  style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3, marginTop: 3 }}
                >
                  Unlock Coach Kiley's voice notes
                </p>
                <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 4 }}>
                  Move from Seedling to Rooted ($97/mo) for weekly check-ins, voice replies, and live circles.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  {/* Had no onClick and no wrapping Link: the app's primary
                      upgrade CTA was inert. "Not now" beside it worked, so the
                      only working control on the card was the one that dismisses
                      it. /billing is the real plan chooser (Convex + Stripe). */}
                  <Link href="/billing" className="no-underline" data-testid="link-see-rooted">
                    <Btn variant="primary" size="sm" style={{ background: "var(--mnf-gold)", borderColor: "var(--mnf-gold)" }}>
                      See Rooted
                    </Btn>
                  </Link>
                  <button
                    onClick={() => setShowUpgrade(false)}
                    data-testid="button-not-now"
                    className="mnf-body transition-all"
                    style={{
                      padding: "7px 12px", borderRadius: 999,
                      background: "transparent", color: "var(--mnf-tg)",
                      fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
                      cursor: "pointer", border: "none",
                    }}
                  >
                    Not today
                  </button>
                </div>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Sisterhood pulse */}
        <SectionTitle eyebrow="Sisterhood" title="Lifting up today" trailing={<Link href="/sisters/prayer-wall" className="no-underline" style={{ color: "var(--mnf-t)" }}>Prayer wall</Link>} />
        <div className="space-y-3">
          {prayerPulse.map((p) => {
            const ring = p.theme === "grief" ? "rgba(127,119,221,0.55)"
              : p.theme === "marriage" ? "rgba(192,120,74,0.45)"
              : p.theme === "postpartum" ? "rgba(168,212,184,0.55)"
              : p.theme === "motherhood" ? "rgba(160,120,32,0.45)"
              : "rgba(30,74,53,0.25)";
            return (
              <Link key={p.id} href="/sisters/prayer-wall" className="no-underline block" data-testid={`link-prayer-pulse-${p.id}`}>
                <GlassCard padding={16}>
                  <div className="flex items-start gap-3">
                    <Avatar initials={p.authorInitials} size={40} ringColor={ring} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{p.authorName}</p>
                        <PillTag tone="fiber">{p.lifted ? "Praying" : "Lift up"}</PillTag>
                      </div>
                      <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 4 }}>"{p.body}"</p>
                      <div className="flex items-center gap-2 mt-3">
                        <span
                          className="flex items-center gap-1.5"
                          style={{
                            padding: "6px 12px", borderRadius: 999,
                            background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
                            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                          }}
                        >
                          <Icon name="favorite" size={13} /> Praying · {p.liftCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            );
          })}
        </div>

        {/* Share Your Journey */}
        <Link href="/share-journey" className="no-underline block" data-testid="link-share-journey">
          <GlassCard
            organic
            padding={16}
            style={{
              background: "linear-gradient(135deg, rgba(168,212,184,0.22) 0%, rgba(245,240,232,0.7) 100%)",
              border: "1px solid rgba(168,212,184,0.45)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 40, height: 40,
                  borderRadius: "14px 6px 14px 6px",
                  background: "#fff", color: "#2C6248",
                  border: "1px solid rgba(168,212,184,0.55)",
                }}
              >
                <span style={{ fontSize: 20 }} aria-hidden>🌱</span>
              </div>
              <div className="flex-1">
                <p className="mnf-eyebrow" style={{ color: "#2C6248" }}>3 seeds planted</p>
                <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-fd)", marginTop: 2 }}>
                  Share Your Journey
                </p>
              </div>
              <Icon name="arrow_forward" size={18} />
            </div>
          </GlassCard>
        </Link>

        {/* Notification permission nudge, only when she has not answered yet. */}
        {notifPrefs?.permissionStatus === "unknown" && (
          <Link href="/notifications/permission" className="no-underline block" data-testid="link-notification-nudge">
            <GlassCard
              organic
              padding={14}
              className="flex items-center gap-3"
              style={{
                background: "linear-gradient(135deg, rgba(192,120,74,0.12) 0%, rgba(160,120,32,0.10) 100%)",
                border: "1px solid rgba(192,120,74,0.25)",
              }}
            >
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: "rgba(192,120,74,0.18)",
                }}
              >
                <Icon name="notifications_active" size={20} color="#C0784A" />
              </div>
              <div className="flex-1">
                <p className="mnf-eyebrow" style={{ color: "#A07820" }}>A small kindness</p>
                <p
                  className="mnf-body"
                  style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-fd)", marginTop: 2 }}
                >
                  Turn on daily scripture reminders
                </p>
              </div>
              <Icon name="arrow_forward" size={18} />
            </GlassCard>
          </Link>
        )}

        {/* Coach link strip */}
        <Link href="/coach" className="no-underline block">
          <GlassCard padding={14} className="flex items-center gap-3" style={{ background: "linear-gradient(135deg, rgba(30,74,53,0.96) 0%, rgba(22,56,40,0.96) 100%)", color: "#F5F0E8", border: "1px solid rgba(245,240,232,0.06)" }}>
            <div className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(245,240,232,0.12)" }}>
              <Icon name="shield_person" size={20} />
            </div>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)" }}>Coach view</p>
              <p className="mnf-body" style={{ fontSize: 13, fontWeight: 600, color: "#F5F0E8" }}>Open Coach Kiley dashboard</p>
            </div>
            <Icon name="arrow_forward" size={18} />
          </GlassCard>
        </Link>
      </div>

      {/* Policy and Settings footer, quietly tucked at the bottom of Today */}
      <div className="px-5 mt-6 flex items-center justify-center gap-4">
        <Link
          href="/settings"
          className="mnf-body inline-flex items-center gap-1"
          style={{ color: "var(--mnf-tg)", fontSize: 12, opacity: 0.75, textDecoration: "none" }}
          data-testid="link-settings-footer"
        >
          <Icon name="settings" size={13} color="var(--mnf-tg)" />
          Settings
        </Link>
        <span style={{ color: "var(--mnf-tg)", opacity: 0.4, fontSize: 12 }}>·</span>
        <Link
          href="/legal"
          className="mnf-body"
          style={{ color: "var(--mnf-tg)", fontSize: 12, opacity: 0.75, textDecoration: "none" }}
          data-testid="link-policies-footer"
        >
          Our policies and your privacy
        </Link>
      </div>

      {/* Water log sheet */}
      {waterOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150, display: "flex", alignItems: "flex-end" }}
          onClick={() => setWaterOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--mnf-cr, #F5F0E8)", borderRadius: "24px 24px 0 0", padding: "24px 20px 32px", width: "100%", maxWidth: 480, margin: "0 auto" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="mnf-eyebrow">Log water</p>
              <button onClick={() => setWaterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }} aria-label="Close">
                <Icon name="close" size={18} color="var(--mnf-tg)" />
              </button>
            </div>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginBottom: 10 }}>
              {totalOz}oz logged today. Goal ~64oz.
            </p>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[8, 12, 16, 24].map((oz) => (
                <button
                  key={oz}
                  onClick={() => { void addWater(oz); }}
                  className="mnf-body"
                  style={{ padding: "10px 0", borderRadius: 12, border: "1px solid rgba(30,74,53,0.18)", background: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)", cursor: "pointer" }}
                >
                  +{oz}oz
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                value={waterInput}
                onChange={(e) => setWaterInput(e.target.value)}
                placeholder="Custom oz"
                min={1}
                className="mnf-body"
                style={{ flex: 1, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "rgba(255,255,255,0.85)", fontSize: 14, color: "var(--mnf-fd)", outline: "none" }}
              />
              <Btn variant="primary" size="sm" onClick={() => { void saveCustomWater(); }}>Add</Btn>
            </div>
            {hydration && hydration.entries.length > 0 && (
              <div className="mt-4">
                <p className="mnf-eyebrow mb-2">Today</p>
                <div className="space-y-1.5">
                  {hydration.entries.map((e) => (
                    <div key={e._id} className="flex items-center justify-between" style={{ background: "rgba(255,255,255,0.6)", borderRadius: 10, padding: "8px 12px" }}>
                      <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)" }}>+{e.oz}oz</span>
                      <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>{new Date(e.loggedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Steps log sheet */}
      {stepsOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150, display: "flex", alignItems: "flex-end" }}
          onClick={() => setStepsOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--mnf-cr, #F5F0E8)", borderRadius: "24px 24px 0 0", padding: "24px 20px 32px", width: "100%", maxWidth: 480, margin: "0 auto" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="mnf-eyebrow">Log steps</p>
              <button onClick={() => setStepsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }} aria-label="Close">
                <Icon name="close" size={18} color="var(--mnf-tg)" />
              </button>
            </div>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginBottom: 10 }}>
              Enter today&apos;s step count. Pulls from your phone&apos;s health app.
            </p>
            <div className="flex gap-2 items-center mb-2">
              <input
                type="number"
                value={stepsInput}
                onChange={(e) => setStepsInput(e.target.value)}
                placeholder="e.g. 7842"
                min={0}
                className="mnf-body"
                style={{ flex: 1, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "rgba(255,255,255,0.85)", fontSize: 14, color: "var(--mnf-fd)", outline: "none" }}
              />
              <Btn variant="primary" size="sm" onClick={() => { void saveStepsInput(); }}>Save</Btn>
            </div>
            <button
              type="button"
              onClick={() => { setStepsOpen(false); setShortcutOpen(true); }}
              className="mnf-body w-full"
              style={{ background: "rgba(30,74,53,0.06)", border: "1px solid rgba(30,74,53,0.12)", borderRadius: 12, padding: "10px 12px", marginTop: 12, fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)", cursor: "pointer" }}
            >
              📱 Set up iPhone auto-post →
            </button>
          </div>
        </div>
      )}

      {/* Shortcut setup sheet */}
      {shortcutOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150, display: "flex", alignItems: "flex-end" }}
          onClick={() => setShortcutOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--mnf-cr, #F5F0E8)", borderRadius: "24px 24px 0 0", padding: "24px 20px 32px", width: "100%", maxWidth: 520, margin: "0 auto", maxHeight: "82vh", overflowY: "auto" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="mnf-eyebrow">Auto-post from Apple Health</p>
              <button onClick={() => setShortcutOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }} aria-label="Close">
                <Icon name="close" size={18} color="var(--mnf-tg)" />
              </button>
            </div>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginBottom: 14 }}>
              An iPhone Shortcut reads today&apos;s steps from Apple Health and posts to Move Nourish Flourish. Set it to run on a schedule and the Bloom card updates itself.
            </p>

            {!stepToken ? (
              <Btn
                variant="primary"
                block
                size="md"
                disabled={tokenBusy}
                onClick={() => { void handleStepToken("create"); }}
              >
                {tokenBusy ? "Creating…" : "Generate my secret token"}
              </Btn>
            ) : (
              <>
                <p className="mnf-body" style={{ fontSize: 12, fontWeight: 600, color: "var(--mnf-fd)", marginTop: 4 }}>1. Endpoint URL</p>
                <div
                  className="mnf-body"
                  style={{ background: "#fff", border: "1px solid rgba(30,74,53,0.12)", borderRadius: 10, padding: "8px 10px", fontSize: 11, color: stepsPostUrl ? "var(--mnf-fd)" : "var(--mnf-t)", wordBreak: "break-all", margin: "4px 0 12px", fontFamily: "ui-monospace, monospace" }}
                >
                  {stepsPostUrl || "(endpoint unavailable — VITE_CONVEX_URL is not set in this build)"}
                </div>

                <p className="mnf-body" style={{ fontSize: 12, fontWeight: 600, color: "var(--mnf-fd)" }}>2. JSON body to send</p>
                <pre
                  style={{ background: "#fff", border: "1px solid rgba(30,74,53,0.12)", borderRadius: 10, padding: "8px 10px", fontSize: 11, color: "var(--mnf-fd)", margin: "4px 0 12px", fontFamily: "ui-monospace, monospace", whiteSpace: "pre-wrap" }}
                >{`{
  "token": "${stepToken}",
  "steps": <today's step count from Health>
}`}</pre>

                <p className="mnf-body" style={{ fontSize: 12, fontWeight: 600, color: "var(--mnf-fd)" }}>3. iPhone Shortcut recipe</p>
                <ol className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.65, paddingLeft: 18, marginTop: 4 }}>
                  <li>Shortcuts app → <strong>+</strong> → New Shortcut. Name it &quot;Post my steps&quot;.</li>
                  <li>Add action <strong>Find Health Samples</strong>. Sample type: <strong>Steps</strong>. Range: <strong>today</strong>. Sort by Start Date, Order Descending, Limit None.</li>
                  <li>Add <strong>Calculate Statistics</strong> → Total → Health Samples.</li>
                  <li>Add <strong>Get Contents of URL</strong>. URL: the endpoint above. Method <strong>POST</strong>. Request Body <strong>JSON</strong>. Add fields: <code>token</code> (text, the value above) and <code>steps</code> (number, the result from Calculate Statistics).</li>
                  <li>Optional: <strong>Automation</strong> tab → Personal Automation → Time of Day → 9:00 PM → Run Shortcut &quot;Post my steps&quot;. Set Run Without Asking ON.</li>
                </ol>

                <button
                  onClick={() => { void handleStepToken("rotate"); }}
                  disabled={tokenBusy}
                  className="mnf-body w-full mt-3"
                  style={{ background: "none", border: "1px solid rgba(30,74,53,0.18)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "var(--mnf-tg)", cursor: tokenBusy ? "default" : "pointer", opacity: tokenBusy ? 0.6 : 1 }}
                >
                  {tokenBusy ? "Rotating…" : "Rotate token (invalidates old shortcut)"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------- Monthly Garden Walk card ----------------------- */

const FRUIT_LABEL_TODAY: Record<string, string> = {
  love: "Love",
  joy: "Joy",
  peace: "Peace",
  patience: "Patience",
  kindness: "Kindness",
  goodness: "Goodness",
  faithfulness: "Faithfulness",
  gentleness: "Gentleness",
  "self-control": "Self-control",
};

/**
 * Compact Garden Walk card for the Today feed. Shows the current month's
 * fruit, the sister's progress, today's prompt, and a tap target to log
 * a walk in one motion. Becomes a quiet "walked today" note after she
 * logs, so the Today surface stays calm.
 */
function GardenWalkTodayCard({ userId }: { userId: string }) {
  const month = useStoreQuery(queries.currentGardenWalkMonth);
  const stats = useStoreQuery(() =>
    month ? queries.gardenWalkStats(userId, month.id) : null,
  );
  const loggedToday = useStoreQuery(() => queries.hasGardenWalkLoggedToday(userId));

  if (!month || !stats) return null;
  const fruitLabel = FRUIT_LABEL_TODAY[month.fruit] ?? month.fruit;
  const pct = Math.min(1, stats.walks / Math.max(1, stats.target));

  return (
    <Link href="/garden-walk" className="no-underline block" data-testid="link-today-garden-walk">
      <GlassCard
        organic
        padding={18}
        style={{
          background: "linear-gradient(135deg, rgba(168,212,184,0.20) 0%, rgba(245,240,232,0.7) 100%)",
          border: "1px solid rgba(90,140,112,0.28)",
          cursor: "pointer",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="shrink-0 inline-flex items-center justify-center"
            style={{
              width: 48,
              height: 48,
              borderRadius: "20px 8px 20px 8px",
              background: "var(--mnf-f)",
              color: "#F5F0E8",
              fontSize: 22,
            }}
          >
            {month.hero.primary}
          </div>
          <div className="flex-1 min-w-0">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 2 }}>
              Garden Walk · {fruitLabel}
            </p>
            <p
              className="mnf-serif"
              style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}
            >
              {loggedToday ? "You walked today, sister." : "Five gentle minutes today?"}
            </p>
            <p
              className="mnf-body italic"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 4 }}
            >
              {stats.todayPrompt}
            </p>
            <div className="mt-3">
              <div
                style={{
                  height: 6,
                  borderRadius: 999,
                  background: "rgba(30,74,53,0.10)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pct * 100}%`,
                    height: "100%",
                    background: "var(--mnf-f)",
                    transition: "width 360ms ease",
                  }}
                />
              </div>
              <p
                className="mnf-body"
                style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 6 }}
                data-testid="text-today-walks-progress"
              >
                {stats.walks} of {stats.target} walks this month
              </p>
            </div>
          </div>
          <Icon name="chevron_right" size={20} weight={400} color="var(--mnf-tg)" />
        </div>
      </GlassCard>
    </Link>
  );
}

/**
 * Compact "Kiley's Pick for Your Season" card for the Today feed. Hand-picked
 * recipe by Coach Kiley for the sister's life season, with the first sentence
 * of her note as the teaser. Coral and cream tones to differentiate from the
 * forest Garden Walk card above. Tapping leads to /kileys-pick.
 */
function KileysPickTodayCard({ userId }: { userId: string }) {
  const bundle = useStoreQuery(() => queries.kileySeasonPickForUser(userId));
  if (!bundle) return null;

  const firstSentence = (() => {
    const note = bundle.pick.kileyNote.trim();
    const m = note.match(/^[^.!?]*[.!?]/);
    return m ? m[0].trim() : note;
  })();

  return (
    <Link
      href="/kileys-pick"
      className="no-underline block"
      data-testid="link-today-kileys-pick"
    >
      <GlassCard
        organic
        padding={18}
        style={{
          background:
            "linear-gradient(135deg, rgba(192,120,74,0.18) 0%, rgba(245,240,232,0.85) 100%)",
          border: "1px solid rgba(160,120,32,0.28)",
          cursor: "pointer",
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar initials="KM" size={48} ringColor="rgba(160,120,32,0.55)" />
          <div className="flex-1 min-w-0">
            <p
              className="mnf-eyebrow"
              style={{ color: "var(--mnf-gold, #A07820)", marginBottom: 2 }}
            >
              Kiley's Pick for your season
            </p>
            <p
              className="mnf-serif"
              style={{
                fontSize: 17,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.3,
              }}
              data-testid="text-today-kiley-pick-title"
            >
              {bundle.recipe.title}
            </p>
            <p
              className="mnf-body italic"
              style={{
                fontSize: 12.5,
                color: "var(--mnf-tg)",
                lineHeight: 1.5,
                marginTop: 4,
              }}
            >
              Kiley says, "{firstSentence}"
            </p>
            <p
              className="mnf-body"
              style={{
                fontSize: 12,
                color: "var(--mnf-t)",
                marginTop: 8,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              See today's pick
            </p>
          </div>
          <Icon name="chevron_right" size={20} weight={400} color="var(--mnf-tg)" />
        </div>
      </GlassCard>
    </Link>
  );
}
